// Why the session ended, when a wallet event ended it (§3 / §4).
//
// The generic "Login required" prompt is true but useless here: an operator
// who switched MetaMask to another account and found themselves logged out
// needs to know that the switch is what did it, and — more importantly — that
// switching back will not undo it. Both facts are non-obvious, and getting
// them wrong during acceptance looks like a bug in the console.
//
// This renders nothing at all unless the latch in `provider-events.ts` is set,
// so an ordinary "your session expired" or "you have not logged in yet" is
// left to the existing copy.

import { useEffect, useState } from "react";
import {
  getInvalidation,
  subscribeInvalidation,
  type InvalidationState,
} from "../auth/provider-events";
import { Banner } from "../ui/primitives";

/**
 * The operator-facing headline.
 *
 * There is no network variant. Switching the wallet's chain does not end a
 * session — the canonical authority is server-side on 84532 and is re-read
 * there whatever network the browser is pointed at — so a "WRONG NETWORK"
 * re-auth notice would be telling the operator something untrue about why they
 * are being asked to sign in again.
 */
export function reauthHeadline(_state: InvalidationState): string {
  return "RE-AUTHENTICATION REQUIRED";
}

export function reauthDetail(state: InvalidationState): string {
  const was =
    state.previousSigner === null
      ? "The session"
      : `The session for ${state.previousSigner.slice(0, 6)}…${state.previousSigner.slice(-4)}`;
  switch (state.reason) {
    case "account_changed":
      return (
        `${was} was ended because the wallet switched to a different account. ` +
        "Sign in again with the account you intend to use — switching back does not restore it."
      );
    case "disconnected":
      return (
        `${was} was ended because the wallet disconnected or locked. ` +
        "Unlock the wallet and sign in again."
      );
  }
}

export function useInvalidation(): InvalidationState | null {
  const [state, setState] = useState<InvalidationState | null>(getInvalidation);
  useEffect(() => subscribeInvalidation(setState), []);
  return state;
}

export function ReauthNotice() {
  const state = useInvalidation();
  if (state === null) return null;
  return (
    <Banner tone="warn" title={reauthHeadline(state)}>
      {reauthDetail(state)}
    </Banner>
  );
}
